import { LiquidacionAgenciaFilter } from '../../../services/liquidaciones.service';
import { LiquidacionAgenciaFilterDto } from '@opb-workspace/shared';

/**
 * Construcción de los filtros del listado de Liquidaciones Agencia, en UN SOLO lugar.
 *
 * POR QUÉ EXISTE ESTE ARCHIVO
 *
 * El listado en pantalla y la exportación a Excel armaban su objeto de filtros por
 * separado, con las mismas líneas copiadas. El 2026-09-04 OPB reportó que el Excel
 * de la liquidación 1623 salía **sin datos, sólo encabezados y totales en cero**.
 *
 * La causa: la exportación se había quedado atrás. Mandaba cliente, temporada y
 * fechas, pero NO los filtros del punto A.1 —`tipoId`, `nroLiquidacion`,
 * `nroFactura`, `nroNotaCobro`— ni `estadoId`. Cinco filtros perdidos.
 *
 * Y el daño no fue "trae de más": sin `nroLiquidacion`, el backend vuelve a aplicar
 * el filtro de TEMPORADA (lo desactiva a propósito cuando se busca por documento).
 * La liquidación 1623 es de abril de 2025, fuera de la temporada activa, así que
 * desaparecía del conjunto y la intersección con lo seleccionado quedaba vacía.
 *
 * Agregar los cinco campos que faltaban habría arreglado ESTE caso y dejado la
 * puerta abierta al siguiente: el próximo filtro que se agregue al listado vuelve a
 * olvidarse acá. Por eso la construcción vive en una función pura y compartida —
 * si un filtro nuevo entra al listado, entra también a la exportación, sin que
 * nadie tenga que acordarse.
 */

/** Estado de los controles de filtro de la pantalla. */
export interface EstadoFiltrosLiquidaciones {
    clienteId?: number | null;
    /** Viene del `<select>`, por eso es string. `''` significa "sin temporada". */
    temporada?: string | null;
    fechaDesde?: string;
    fechaHasta?: string;
    tipoId?: number | null;
    nroLiquidacion?: string | null;
    nroFactura?: string | null;
    nroNotaCobro?: string | null;
    estadoId?: number | null;
    orderBy?: string;
    orderDirection?: 'ASC' | 'DESC';
}

export interface PaginacionFiltros {
    page: number;
    limit: number;
}

/**
 * Tamaño de página al exportar.
 *
 * ⚠️ NO subirlo. La exportación pedía `limit: 10000` de una sola vez y esa petición
 * **muere por timeout**: medido el 2026-09-04 contra el ambiente de test, `limit`
 * 500, 1000 y 10000 devuelven **502**; con 200 responde. La query del listado lleva
 * subconsultas de documentos por fila y no escala a miles.
 *
 * Por eso la exportación pagina en lugar de pedir todo junto.
 */
export const TAMANO_PAGINA_EXPORT = 200;

/**
 * Tope de páginas de la exportación. Existe para que un error de paginación no
 * quede en un bucle infinito, NO para recortar datos: al alcanzarlo hay que avisar,
 * nunca entregar el archivo como si estuviera completo.
 */
export const MAX_PAGINAS_EXPORT = 60;

/** Normaliza un campo de texto: vacío o sólo espacios equivale a "sin filtro". */
const texto = (valor?: string | null): string | undefined =>
    valor?.trim() || undefined;

/**
 * Arma el objeto de filtros que se manda al backend.
 *
 * La MISMA función alimenta el listado y la exportación; lo único que cambia entre
 * los dos es la paginación.
 */
export function construirFiltrosLiquidaciones(
    estado: EstadoFiltrosLiquidaciones,
    paginacion: PaginacionFiltros
): LiquidacionAgenciaFilter {
    return {
        page: paginacion.page,
        limit: paginacion.limit,
        orderBy: estado.orderBy,
        orderDirection: estado.orderDirection,
        clienteId: estado.clienteId ?? undefined,
        // El `<select>` entrega string. `''` (sin temporada) NO debe convertirse en
        // NaN: `parseInt('')` da NaN y el backend lo recibiría como filtro basura.
        temporada: estado.temporada
            ? parseInt(estado.temporada, 10)
            : undefined,
        fechaDesde: estado.fechaDesde,
        fechaHasta: estado.fechaHasta,
        // Punto A.1 (OPB, ago-2026). 🔴 Estos cuatro son los que faltaban en la
        // exportación y provocaron el Excel vacío de la liquidación 1623.
        tipoId: estado.tipoId ?? undefined,
        nroLiquidacion: texto(estado.nroLiquidacion),
        nroFactura: texto(estado.nroFactura),
        nroNotaCobro: texto(estado.nroNotaCobro),
        estadoId: estado.estadoId ?? undefined,
    };
}

/**
 * ¿Se está buscando por un documento concreto?
 *
 * Importa porque el backend **desactiva el filtro de temporada** cuando la búsqueda
 * es por documento: un N° de liquidación o de factura identifica una fila puntual, y
 * acotarla además por temporada la haría desaparecer si es de un período anterior.
 *
 * Se replica acá para poder ADVERTIR en la exportación, no para decidir el filtrado
 * —eso lo hace el backend—.
 */
export function buscaPorDocumento(estado: EstadoFiltrosLiquidaciones): boolean {
    return Boolean(
        texto(estado.nroLiquidacion) ||
            texto(estado.nroFactura) ||
            texto(estado.nroNotaCobro)
    );
}

/**
 * Deja sólo las filas cuyo id está seleccionado con el check (ERR-01: al exportar
 * una selección, el Excel debe traer SÓLO esas).
 *
 * 🔴 ESTA INTERSECCIÓN PUEDE QUEDAR VACÍA, y ahí estuvo el daño de la liq 1623: si
 * lo seleccionado no aparece en el universo que devolvió el backend, no queda
 * ninguna fila. Quien la llame TIENE que mirar el largo del resultado antes de
 * generar el archivo — un Excel con cero filas se ve igual de válido que uno bueno.
 *
 * Está acá, y no en línea dentro del componente, para poder probar justamente ese
 * caso sin levantar Angular.
 */
export function filtrarPorSeleccion<T extends { id: number }>(
    filas: readonly T[],
    idsSeleccionados: readonly number[]
): T[] {
    if (idsSeleccionados.length === 0) return [...filas];
    const seleccionados = new Set(idsSeleccionados);
    return filas.filter((fila) => seleccionados.has(fila.id));
}

/**
 * MEJ-03b: traduce los filtros del listado (`LiquidacionAgenciaFilter`, con
 * `fechaDesde`/`fechaHasta`) al DTO que espera el backend para el modo
 * "filtros" del Excel Facturación (`POST /liquidaciones-agencia/facturacion`,
 * MISMO DTO que el GET del listado — `dateFrom`/`dateTo`).
 *
 * NO incluye `page`/`limit`/`ids`: el backend fija su propia paginación según
 * el modo (`LiquidacionAgenciaFacturacionService.resolveFilters`) — mandarlos
 * acá sería repetir la bifurcación page/limit que ya resuelve el backend.
 */
export function mapFiltrosParaFacturacion(
    filtros: LiquidacionAgenciaFilter
): LiquidacionAgenciaFilterDto {
    return {
        clienteId: filtros.clienteId,
        temporada: filtros.temporada,
        tipoId: filtros.tipoId,
        nroLiquidacion: texto(filtros.nroLiquidacion),
        nroFactura: texto(filtros.nroFactura),
        nroNotaCobro: texto(filtros.nroNotaCobro),
        estadoId: filtros.estadoId,
        dateFrom: filtros.fechaDesde,
        dateTo: filtros.fechaHasta,
        tipoServicio: filtros.tipoServicio,
        comisionMinima: filtros.comisionMinima,
        comisionMaxima: filtros.comisionMaxima,
        estado: filtros.estado,
        orderBy: filtros.orderBy,
        orderDirection: filtros.orderDirection,
    };
}

/**
 * Body de `POST /liquidaciones-agencia/facturacion` (D11): selección
 * explícita del listado → `{ ids }`; sin selección → `{ filtros }` con el
 * filtro ACTUAL del listado, sin paginar (spec `agency-billing-export`,
 * "Export Scope"). Nunca se arman los dos modos a la vez.
 */
export function buildFacturacionRequestBody(
    idsSeleccionados: readonly number[],
    filtrosActuales: LiquidacionAgenciaFilter
): { ids: number[] } | { filtros: LiquidacionAgenciaFilterDto } {
    if (idsSeleccionados.length > 0) {
        return { ids: [...idsSeleccionados] };
    }
    return { filtros: mapFiltrosParaFacturacion(filtrosActuales) };
}

/**
 * Visibilidad del botón "Excel Facturación" (spec `agency-billing-export`,
 * "Excel Facturación Restringido a Admin y Operador"): SOLO admin u
 * operator. El backend es quien realmente rechaza `client` (403, D11); esto
 * es exclusivamente UX — ocultar un botón que igual sería rechazado.
 *
 * Función pura (no depende de `AuthService`) para poder probar los tres
 * roles sin levantar Angular — el componente real
 * (`liquidaciones-agencia.component.spec.ts`) está excluido de Jest (deuda
 * de sintaxis Jasmine, ver `jest.config.ts`).
 */
export function puedeVerBotonFacturacion(
    hasRole: (role: string) => boolean
): boolean {
    return hasRole('admin') || hasRole('operator');
}
